import React, { useState, useEffect, useMemo } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import { Link as ReactRouterLink } from 'react-router-dom';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { onError } from '../../../utils/error.util';
import {
  AdminDoc,
  AdminDocDivision,
  AdminDocStatus,
  ADMIN_DOC_DEPARTMENTS,
  AdminDocDepartment,
} from '../../../models/admin-doc.model';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import Table from '@mui/material/Table';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import {
  deleteAdminDoc,
  getAdminDocList,
} from '../../../services/admin-docs.service';
import { CircularProgressComponent } from '../../../components/admin/circular-progress.component';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';

const LIMIT = 100;

function formatDate(timestamp: number) {
  return new Date(timestamp).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function divisionLabel(division: AdminDocDivision) {
  switch (division) {
    case AdminDocDivision.OnboardingCCL:
      return 'CCL Onboarding';
    case AdminDocDivision.OnboardingTSF:
      return 'TSF Onboarding';
    case AdminDocDivision.CCL:
      return 'CCL';
    case AdminDocDivision.TSF:
      return 'TSF';
    case AdminDocDivision.Company:
      return 'Company';
    default:
      return 'N/A';
  }
}

function statusLabel(status: AdminDoc['status']) {
  return status === AdminDocStatus.Done ? 'Done' : 'Open';
}

/** Sort docs by department order (Admin, Analyst, …) then by date DESC within each department */
function sortByDepartmentThenDate(docs: AdminDoc[]): AdminDoc[] {
  const order = (d: AdminDoc) => {
    const dept = d.department ?? (ADMIN_DOC_DEPARTMENTS[0] as AdminDocDepartment);
    const i = ADMIN_DOC_DEPARTMENTS.indexOf(dept);
    return i >= 0 ? i : 999;
  };
  return [...docs].sort((a, b) => {
    const oa = order(a);
    const ob = order(b);
    if (oa !== ob) return oa - ob;
    return b.createdAt - a.createdAt;
  });
}

function AdminDocDescriptionRow({ description }: { description: string }) {
  return (
    <TableRow sx={{ '& td': { borderTop: 'none' } }}>
      <TableCell colSpan={6} sx={{ py: 1.5 }}>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </TableCell>
    </TableRow>
  );
}

export default function AdminDocsContainer() {
  const [docs, setDocs] = useState<AdminDoc[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [docToDelete, setDocToDelete] = useState<string | undefined>(undefined);

  useEffect(() => {
    loadDocs();
  }, []);

  async function loadDocs() {
    try {
      setIsLoading(true);
      const result = await getAdminDocList({ limit: LIMIT });
      setDocs(result.items);
    } catch (e: any) {
      onError(e);
    } finally {
      setIsLoading(false);
    }
  }

  function handleShowDeleteModal(id: string) {
    setDocToDelete(id);
    setIsDeleteModalOpen(true);
  }

  function handleCloseDeleteModal() {
    setIsDeleteModalOpen(false);
    setDocToDelete(undefined);
  }

  async function handleDelete() {
    if (!docToDelete) return;
    try {
      await deleteAdminDoc(docToDelete);
      handleCloseDeleteModal();
      loadDocs();
    } catch (e: any) {
      onError(e);
    }
  }

  const byDivision = useMemo(() => {
    const company: AdminDoc[] = [];
    const ccl: AdminDoc[] = [];
    const tsf: AdminDoc[] = [];
    const onboardingCcl: AdminDoc[] = [];
    const onboardingTsf: AdminDoc[] = [];
    for (const d of docs) {
      if (d.division === AdminDocDivision.Company) company.push(d);
      else if (d.division === AdminDocDivision.CCL) ccl.push(d);
      else if (d.division === AdminDocDivision.TSF) tsf.push(d);
      else if (d.division === AdminDocDivision.OnboardingCCL) onboardingCcl.push(d);
      else if (d.division === AdminDocDivision.OnboardingTSF) onboardingTsf.push(d);
    }
    company.sort((a, b) => b.createdAt - a.createdAt);
    return { company, ccl, tsf, onboardingCcl, onboardingTsf };
  }, [docs]);

  const onboardingCclSorted = useMemo(() => sortByDepartmentThenDate(byDivision.onboardingCcl), [byDivision.onboardingCcl]);
  const onboardingTsfSorted = useMemo(() => sortByDepartmentThenDate(byDivision.onboardingTsf), [byDivision.onboardingTsf]);
  const cclSorted = useMemo(() => sortByDepartmentThenDate(byDivision.ccl), [byDivision.ccl]);
  const tsfSorted = useMemo(() => sortByDepartmentThenDate(byDivision.tsf), [byDivision.tsf]);

  function renderCompanyTable() {
    return (
      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Typography variant="h6" component="h2" sx={{ px: 2, pt: 2, pb: 1 }}>
          Company
        </Typography>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Division</TableCell>
              <TableCell>Department</TableCell>
              <TableCell>Created Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {byDivision.company.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No docs for this division
                </TableCell>
              </TableRow>
            ) : (
              byDivision.company.map((row) => (
                <React.Fragment key={row.id}>
                  <TableRow sx={row.description ? { '& td': { borderBottom: 'none' } } : undefined}>
                    <TableCell>
                      <ReactRouterLink to={`/admin-docs/${row.id}`}>
                        <span>{row.name}</span>
                      </ReactRouterLink>
                    </TableCell>
                    <TableCell>{divisionLabel(row.division)}</TableCell>
                    <TableCell>{row.department ?? '—'}</TableCell>
                    <TableCell>{formatDate(row.createdAt)}</TableCell>
                    <TableCell>{statusLabel(row.status)}</TableCell>
                    <TableCell align="left">
                      <Tooltip title="Delete">
                        <ReactRouterLink onClick={() => handleShowDeleteModal(row.id)} to="">
                          <DeleteIcon color="error" fontSize="small" />
                        </ReactRouterLink>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  {row.description ? (
                    <AdminDocDescriptionRow description={row.description} />
                  ) : null}
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  function renderDivisionTable(title: string, items: AdminDoc[]) {
    return (
      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Typography variant="h6" component="h2" sx={{ px: 2, pt: 2, pb: 1 }}>
          {title}
        </Typography>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Division</TableCell>
              <TableCell>Department</TableCell>
              <TableCell>Created Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No docs for this division
                </TableCell>
              </TableRow>
            ) : (
              items.map((row) => (
                <React.Fragment key={row.id}>
                  <TableRow sx={row.description ? { '& td': { borderBottom: 'none' } } : undefined}>
                    <TableCell>
                      <ReactRouterLink to={`/admin-docs/${row.id}`}>
                        <span>{row.name}</span>
                      </ReactRouterLink>
                    </TableCell>
                    <TableCell>{divisionLabel(row.division)}</TableCell>
                    <TableCell>{row.department ?? '—'}</TableCell>
                    <TableCell>{formatDate(row.createdAt)}</TableCell>
                    <TableCell>{statusLabel(row.status)}</TableCell>
                    <TableCell align="left">
                      <Tooltip title="Delete">
                        <ReactRouterLink onClick={() => handleShowDeleteModal(row.id)} to="">
                          <DeleteIcon color="error" fontSize="small" />
                        </ReactRouterLink>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  {row.description ? (
                    <AdminDocDescriptionRow description={row.description} />
                  ) : null}
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return (
    <>
      <main id="main">
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <h1>Admin Docs</h1>
              <ReactRouterLink to="/admin-docs/new">
                <Button variant="contained" color="primary">
                  Add New
                </Button>
              </ReactRouterLink>
            </Box>
          </Grid>

          <Grid size={{ xs: 12 }}>
            {isLoading ? (
              <CircularProgressComponent />
            ) : (
              <>
                {renderCompanyTable()}
                {renderDivisionTable('CCL', cclSorted)}
                {renderDivisionTable('TSF', tsfSorted)}
                {renderDivisionTable('CCL Onboarding', onboardingCclSorted)}
                {renderDivisionTable('TSF Onboarding', onboardingTsfSorted)}
              </>
            )}
          </Grid>
        </Grid>
      </main>

      <Dialog
        open={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this admin doc? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteModal}>Cancel</Button>
          <Button onClick={handleDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
